import React, { useState, useEffect } from 'react';
import { DateTime } from 'luxon';
import {
    Card,
    CardContent,
    Badge,
    Typography,
    Box,
    Avatar,
    IconButton
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import UserOnlineStatus from 'components/Modals/UserOnlineChecker';
import { useTranslation } from "react-i18next";
import '../../css/components/Profile/ProfileCard.css';
import '../../css/main.css';

import { getUserStatusPriority } from '../Custom/userStatusHelper';
import { ConvertToMyTimeZone } from '../Custom/TimezoneConvertion';

import TimezoneDisplay from './TimezoneDisplay';

const ProfileCard = ({ user, workingHours, isEditable, selectedFile, handleFileChange, myUserTimezone }) => {
    const { t } = useTranslation();
    const [showLastOnline, setShowLastOnline] = useState(false);

    const getCountryFlag = (location_string) => {
        const regex = /\(([^)]+)\)/;
        const matches = regex.exec(location_string);
        if (matches && matches.length > 1) {
            return matches[1].toLocaleLowerCase();
        }
    };

    // Append workingHours to the user object before passing it to getUserStatusPriority
    const userWithWorkingHours = { ...user, working_hours: workingHours };

    // Get status priority and message for the user
    const { status: statusPriority, message } = getUserStatusPriority(userWithWorkingHours, new Date(), t);

    const calculateTimeAgo = () => {
        if (!user?.last_online || !myUserTimezone?.timezone) return;

        try {
            const userLastOnline = ConvertToMyTimeZone(user.last_online, myUserTimezone.timezone);
            const now = DateTime.now().setZone(myUserTimezone.timezone);

            if (!userLastOnline || !userLastOnline.isValid) {
                setShowLastOnline(true); // Show last online if timestamp is invalid
                return;
            }

            const diff = now.diff(userLastOnline, ['minutes']);
            const minutesAgo = Math.floor(diff.minutes);

            if (minutesAgo < 0) {
                setShowLastOnline(true); // Show last online if timestamp is in the future
                return;
            }

            // Only show last online if it's been more than 15 minutes
            if (minutesAgo > 15) {
                setShowLastOnline(true);
            } else {
                setShowLastOnline(false);
            }
        } catch (error) {
            setShowLastOnline(true); // Show last online if there's an error
        }
    };

    //this is just a cool neat lil "feature", it just updates minute, by minute localy if you leave the window open
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        calculateTimeAgo();
        const interval = setInterval(calculateTimeAgo, 60000);
        return () => clearInterval(interval);
    }, [user?.last_online, myUserTimezone?.timezone]);


    return (
        <Card className="profile-card-card">
            <CardContent className="profile-card-content">
                {isEditable ? (
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                            <IconButton component="label">
                                <CloudUploadIcon className="white-txt-color" />
                                <input
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={handleFileChange}
                                />
                            </IconButton>
                        }
                    >
                        <Avatar
                            src={selectedFile ? URL.createObjectURL(selectedFile) : user.image}
                            alt="avatar"
                            className="profile-card-avatar"
                        />
                    </Badge>
                ) : (
                    <Avatar
                        src={selectedFile ? URL.createObjectURL(selectedFile) : user.image}
                        alt="avatar"
                        className="profile-card-avatar"
                    />
                )}
                <Typography variant="h5" className="white-txt-color">
                    {user.full_name?.split(' ').map((name, index, array) => {
                        if (index === 0) {
                            return name; // First name
                        } else if (index === array.length - 1) {
                            return ` "${user.username}" ${name}`; // Last name
                        } else {
                            return ' '; // Space between first name and username, and between username and last name
                        }
                    })}
                </Typography>
                <Typography variant="h6" className="white-txt-color profile-card-job-title-margin">{user.job}</Typography>
                {user.location && (
                    <Typography variant="body2" className="white-txt-color">
                        {user.location}
                        <img
                            src={`https://flagcdn.com/w80/${getCountryFlag(user.location)}.png`}
                            srcSet={`https://flagcdn.com/w80/${getCountryFlag(user.location)}.png 2x`}
                            alt="Flag"
                            className="profile-card-flag-image"
                        />
                    </Typography>
                )}

                <TimezoneDisplay user={user} myUserTimezone={myUserTimezone.timezone} />

                <Box className="profile-card-online-status-box">
                    {myUserTimezone?.timezone && myUserTimezone?.timezone_offset && user?.last_online && (
                        <UserOnlineStatus
                            userStatusAvailability={user.current_availability}
                            statusPriority={statusPriority}
                            message={message}
                            showBadgeOnly={false}
                        />
                    )}
                </Box>

                <Box className="profile-card-online-status-box">
                    {myUserTimezone?.timezone && user?.last_online && (
                        <>
                            <UserOnlineStatus
                                userStatusAvailability={user.current_availability}
                                statusPriority={statusPriority}
                                message={showLastOnline ? message : t('working_hours.status_online')} // Show "Online" if within 15 minutes
                                showBadgeOnly={false}
                            />
                        </>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default ProfileCard;
